import React, {useContext, useMemo, useState} from 'react';
import {ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import AntDesign from '@expo/vector-icons/AntDesign';
import {useTranslation} from "react-i18next";
import HappyAlert from '../helpers/crossPlatformTools/HappyAlert';
import {getAccessToken} from "../helpers/storage";
import axios from "axios";
import {ApiConfig} from "../config/ApiConfig";
import Observables from "@happy-gastro/global-observables";
import {BluetoothContext} from "../helpers/BluetoothContext";
import ErrorReportingService from '../helpers/ErrorReportingService';
import FiscatQRService from '../helpers/FiscatQRService';
import {priceFormatter} from '../helpers/numbers';

const SuccessModal = ({ visible, order, onClose }) => {

    const [isAlertVisible, setIsAlertVisible] = useState(false); // State for HappyAlert visibility
    const { t } = useTranslation();
    const { sendData } = useContext(BluetoothContext);
    const vatSummary = useMemo(() => FiscatQRService.getVatSummary(order), [order]);

    if (!visible) return null;

    const handleCloseInvoice = () => {
        setIsAlertVisible(true);
    };

    const handleOrderClose = async () => {
        try {
            if (order?._id) {
                const token = await getAccessToken();
                await axios.get(ApiConfig.apiURL + `/api/order/${order._id}/status/closed`,  {
                    headers: {
                        "Accept-Language": "hu_HU",
                        "authorization": `Bearer ${token}`,
                    },
                });
                Observables.call("reloadOrders");
            }
        } catch (error) {
            ErrorReportingService.warn('Failed to close order from SuccessModal, closing UI anyway', {
                orderId: order?._id,
                error: error?.message,
            }, 'SuccessModal');
        } finally {
            onClose?.();
        }
    };

    return (
        <View style={styles.backdrop}>
            <View style={styles.modal}>
                <ScrollView
                    style={styles.scrollArea}
                    contentContainerStyle={styles.scrollAreaContent}
                    showsVerticalScrollIndicator={true}
                >
                <Text style={[styles.logo, {}]}>{t('order_success')}</Text>
                <View style={styles.content}>
                    <AntDesign name="checkcircle" size={68} color="#FFF" style={{paddingBottom: 30}}/>
                    <Text style={[styles.logo, styles.orderId]}>{t('order_id')}: #{order.orderCode || ""}</Text>

                    {order?.orderType === "takeAway" &&
                            <TouchableOpacity style={[styles.button, styles.fullWidthButton]} onPress={onClose}>
                                <Text style={styles.buttonText}>{t('save_for_later')}</Text>
                            </TouchableOpacity>
                    }
                    {order?.orderType === "takeAway" && order?.userInfo &&
                            <TouchableOpacity style={[styles.orangeButton, styles.fullWidthButton]}
                                              onPress={handleCloseInvoice}>
                                <Text style={styles.buttonText}>{t('close_invoice')}</Text>
                            </TouchableOpacity>

                }
                {(order?.orderType !== "takeAway" || (order?.orderType === "takeAway" && !order?.userInfo)) &&
                            <TouchableOpacity style={[styles.orangeButton, styles.fullWidthButton]}
                                              onPress={handleOrderClose}>
                                <Text style={styles.buttonText}>{t('close')}</Text>
                            </TouchableOpacity>

                }
                    <View style={styles.vatContainer}>
                        <Text style={styles.vatTitle}>{t('vat_breakdown')}</Text>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={true}
                            contentContainerStyle={styles.vatTableScrollContent}
                        >
                            <View style={styles.vatTable}>
                                <View style={styles.vatHeaderRow}>
                                    <Text style={styles.vatHeaderCell}>{t('vat_rate')}</Text>
                                    <Text style={styles.vatHeaderCell}>{t('net_amount')}</Text>
                                    <Text style={styles.vatHeaderCell}>{t('vat_amount')}</Text>
                                    <Text style={styles.vatHeaderCell}>{t('gross_amount')}</Text>
                                </View>
                                {vatSummary.length > 0 ? vatSummary.map((summaryRow, index) => (
                                    <View
                                        key={`success-modal-vat-${summaryRow.vatRate}-${index}`}
                                        style={[
                                            styles.vatDataRow,
                                            index < vatSummary.length - 1 ? styles.vatDataRowBorder : null,
                                        ]}
                                    >
                                        <Text style={styles.vatDataCell}>{FiscatQRService.vatRowDisplayLabel(summaryRow, t)}</Text>
                                        <Text style={styles.vatDataCell}>{priceFormatter(summaryRow.net)}</Text>
                                        <Text style={styles.vatDataCell}>{priceFormatter(summaryRow.vat)}</Text>
                                        <Text style={styles.vatDataCell}>{priceFormatter(summaryRow.gross)}</Text>
                                    </View>
                                )) : (
                                    <View style={styles.vatDataRow}>
                                        <Text style={styles.vatEmptyCell}>{t('vat_breakdown_not_available')}</Text>
                                    </View>
                                )}
                            </View>
                        </ScrollView>
                    </View>
                    <View style={{height: 1, backgroundColor: '#404040', width: "100%", marginVertical: 8}}/>
                    <TouchableOpacity style={[styles.button, styles.fullWidthButton, {
                        marginTop: 10,
                        marginBottom: 10,
                        backgroundColor: "black"
                    }]} onPress={() => {
                        new Promise(() => sendData('fiscat/AEE|PEJ|4'))
                    }}>
                        <Text style={[styles.buttonText, {color: 'white'}]}>{t("reprint_invoice")}</Text>
                    </TouchableOpacity>
                </View>
                </ScrollView>
            </View>
            <HappyAlert
                visible={isAlertVisible}
                onConfirm={() => {
                    setIsAlertVisible(false);
                    handleOrderClose();
                }}
                onCancel={() => setIsAlertVisible(false)}
                type="info"
                message={t('confirm_close_order')}
                confirmText={t('yes')}
                cancelText={t('no')}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modal: {
        padding: 16,
        minHeight: "fit-content",
        backgroundColor: '#81F174',
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        width: '76%',
        maxWidth: 760,
        maxHeight: '88%',
    },
    scrollArea: {
        width: '100%',
    },
    scrollAreaContent: {
        alignItems: 'center',
    },
    content: {
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    logo: {
        fontSize: 34,
        fontWeight: 'bold',
        color: '#FFF',
        textAlign: 'center',
    },
    orderId: {
        fontSize: 24,
        textAlign: 'center',
        color: 'green',
        fontWeight: "bold",
        marginBottom: 18,
        marginTop: 8,
    },
    button: {
        backgroundColor: 'green',
        paddingVertical: 15,
        paddingHorizontal: 15,
        borderRadius: 5,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
    },
    buttonText: {
        color: 'white',
        fontSize: 16,
    },
    orangeButton: {
        backgroundColor: '#FFA500',
        paddingHorizontal: 15,
        paddingVertical: 15,
        borderRadius: 5,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        marginTop: 10,
        marginBottom: 10,
    },
    fullWidthButton: {
        width: '100%',
    },
    vatContainer: {
        width: '100%',
        marginTop: 8,
        marginBottom: 8,
    },
    vatTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#245f12',
        marginBottom: 8,
        textAlign: 'left',
        width: '100%',
    },
    vatTableScrollContent: {
        paddingBottom: 4,
    },
    vatTable: {
        minWidth: 520,
        width: '100%',
        borderWidth: 1,
        borderColor: 'rgba(36,95,18,0.2)',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'rgba(255,255,255,0.78)',
    },
    vatHeaderRow: {
        flexDirection: 'row',
        backgroundColor: 'rgba(255,255,255,0.55)',
        paddingVertical: 10,
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(36,95,18,0.15)',
    },
    vatHeaderCell: {
        flex: 1,
        textAlign: 'center',
        fontWeight: '700',
        color: '#245f12',
        fontSize: 13,
    },
    vatDataRow: {
        flexDirection: 'row',
        paddingVertical: 8,
        paddingHorizontal: 8,
    },
    vatDataRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(36,95,18,0.12)',
    },
    vatDataCell: {
        flex: 1,
        textAlign: 'center',
        color: '#1f2937',
        fontSize: 13,
        fontWeight: '600',
    },
    vatEmptyCell: {
        flex: 1,
        minWidth: 600,
        textAlign: 'center',
        color: '#4b5563',
        fontSize: 14,
        fontStyle: 'italic',
    },
});

export default SuccessModal;